import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './InformationForm.module.scss';
import { Col, Row } from 'react-flexbox-grid';
import Label from 'common/components/label/Label';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { UseFormMethods, useWatch } from 'react-hook-form';
import FormItem from 'common/components/formItem/FormItem';
import InputError from 'common/components/inputError/InputError';
import DateTimePickerControllerNew from 'common/components/dateTimePicker/DateTimePickerControllerNew';
import SelectController from 'common/components/select/SelectController';
import InputGroupController from 'common/components/inputGroup/InputGroupController';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE, removeAccents, SEGMENT_FEATURES } from 'Config';
import { VerificationType } from 'api/carVerification/enums/VerificationType';
import { VerificationViewModel } from 'api/carVerification/models/VerificationViewModel';
import { isTripLikeVerification } from 'common/utils/VerificationsUtils';
import Utils from 'common/services/Utils';
import { FaInfoCircle, FaRegClock } from 'react-icons/fa';
import Timekeeper from 'react-timekeeper';
import dayjs from 'dayjs';
import { VehicleDto } from 'api/vehicles/models/VehicleDto';
import VehiclesService from 'api/vehicles/VehiclesService';
import Logger from 'common/services/Logger';
import { useToasts } from 'react-toast-notifications';
import InputInfoWarning from 'common/components/inputInfoWarning/InputInfoWarning';
import TrackingService from 'api/tracking/TrackingService';
import { TrackingProvider } from 'api/tracking/enums/TrackingProvider';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { UserProfile } from 'api/account/models/UserProfile';
import { useParams } from 'react-router-dom';
import { TimeKeeperDoneButton } from 'common/components/timeKeeperDoneButton/TimeKeeperDoneButton';
import useCloseWhenClickOutside from 'common/hooks/useCloseWhenClickOutside';
import ToggleSwitch from 'common/components/toggleSwitch/ToggleSwitch';
import ReactTooltip from 'react-tooltip';

type Props = {
    form: UseFormMethods<VerificationViewModel>;
    vehicles: SelectValueLabel[];
    vehicle?: VehicleDto;
    responsables: SelectValueLabel[];
    drivers: SelectValueLabel[];
    genericPage: boolean;
    isDetails: boolean;
    type: string;
    findOverlaps?: () => void;
};

const InformationForm: React.FC<Props> = ({ form, vehicles, vehicle, responsables, drivers, genericPage, isDetails, type, findOverlaps }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();
    const { errors } = form;
    const [showTime, setShowTime] = useState(false);
    const [showTimeFinish, setShowTimeFinish] = useState(false);
    const [currentKmsGps, setCurrentKmsGps] = useState<number | null>(null);
    const { itemType, type: urlType } = useParams<{ itemType: string, type: string }>();

    const typeWatch = useWatch({ control: form.control, name: 'type' });
    const verificationFinishDateWatch = useWatch<Date | null>({ control: form.control, name: 'verificationFinishDate' });
    const [showKilometersFinish, setShowKilometersFinish] = useState(!!verificationFinishDateWatch);
    const [vehicleParam, setVehicleParam] = useState<VehicleDto | undefined>(vehicle);
    const [inputKmsBelowCurrentVehicleKms, setInputKmsBelowCurrentVehicleKms] = useState<boolean>(false);
    const [finishKmsBelowCurrentVehicleKms, setFinishKmsBelowCurrentVehicleKms] = useState<boolean>(false);
    const [inputHoursBelowCurrentVehicleHours, setInputHoursBelowCurrentVehicleHours] = useState<boolean>(false);
    const [finishHoursBelowCurrentVehicleHours, setFinishHoursBelowCurrentVehicleHours] = useState<boolean>(false);

    const [typesOptions, setTypesOptions] = useState<SelectValueLabel[]>([]);

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasReadGPSKmsPolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_GPS_KMS_READ']);

    const changeVehicle = (data: SelectValueLabel) => {
        const vId = data ? data.value : null;
        getVehicle(vId);
    }

    const getVehicle = async (id?: string | null) => {
        try {
            if (id) {
                const result = await VehiclesService.getById(id);
                if (result) {
                    setVehicleParam(result);
                    if (urlType == 'new') {
                        const hasKm = (result.segmentFeatures?.findIndex(x => x == SEGMENT_FEATURES.KM) ?? -1) >= 0;
                        const hasHours = (result.segmentFeatures?.findIndex(x => x == SEGMENT_FEATURES.HOURS) ?? -1) >= 0;
                        if (!hasKm && !hasHours) {
                            form.setValue('kilometersFinish', undefined);
                            form.setValue('verificationFinishDate', undefined);
                            form.setValue('verificationFinishHour', undefined);
                            form.setValue('hoursMeter', null);
                            form.setValue('hoursMeterFinish', null);
                            form.setValue('type', undefined);
                            setShowKilometersFinish(false);
                            setFinishKmsBelowCurrentVehicleKms(false);
                        } else if (!hasKm) {
                            form.setValue('kilometersFinish', undefined);
                            setShowKilometersFinish(false);
                            setFinishKmsBelowCurrentVehicleKms(false);
                        } else if (!hasHours) {
                            form.setValue('hoursMeter', null);
                            form.setValue('hoursMeterFinish', null);
                        }
                    }
                }
            }

        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get vehicle data`, error);
            addToast(t('common.messages.error_load_info'), { appearance: 'error' });
        }
    }

    const getVehicleGpsCurrentKms = async () => {
        const vehicleAux = vehicle ? vehicle : vehicleParam ? vehicleParam : undefined;
        if (vehicleAux && vehicleAux.id && vehicleAux.companyTrackingProvider && vehicleAux?.companyTrackingProvider !== TrackingProvider.NONE && vehicleAux.trackingDeviceId) {
            try {
                const positionResult = await TrackingService.getGpsVehiclesPosition([vehicleAux.id]);
                if (positionResult && positionResult.length > 0) {
                    setCurrentKmsGps(positionResult[0]?.km ?? null);
                }
            } catch (error) {
                setCurrentKmsGps(null)
                Logger.error(LOGGER_LOG_TYPE.VERIFICATIONS, `Couldn't get vehicle gps current Kms`, error);
                addToast(t('common.messages.error_load_info'), { appearance: 'error' });
            }
        }
    }

    const compareKms = () => {
        if (vehicleParam && (form.watch('kilometers') ?? 0) < (vehicleParam?.currentKilometers ?? 0)) {
            setInputKmsBelowCurrentVehicleKms(true);
        } else {
            setInputKmsBelowCurrentVehicleKms(false);

        }

        if (vehicleParam && (form.watch('kilometersFinish') ?? 0) < (vehicleParam?.currentKilometers ?? 0)) {
            setFinishKmsBelowCurrentVehicleKms(true);
        } else {
            setFinishKmsBelowCurrentVehicleKms(false);
        }
    }

    const compareHours = () => {
        if (vehicleParam && (form.watch('hoursMeter') ?? 0) < (vehicleParam?.currentHours ?? 0)) {
            setInputHoursBelowCurrentVehicleHours(true);
        } else {
            setInputHoursBelowCurrentVehicleHours(false);
        }

        if (vehicleParam && (form.watch('hoursMeterFinish') ?? 0) < (vehicleParam?.currentHours ?? 0)) {
            setFinishHoursBelowCurrentVehicleHours(true);
        } else {
            setFinishHoursBelowCurrentVehicleHours(false);
        }
    }

    const showField = (feature: string) => {
        return (vehicleParam?.segmentFeatures?.findIndex(x => x == feature) ?? -1) >= 0;
    }

    const typesList: SelectValueLabel[] = Utils.sortAlphabetically(Object.keys(VerificationType).map(type => {
        return { value: type, label: t(`car_verification.annotation_types.${type}`.toLowerCase() as any) }
    }));

    useEffect(() => {
        if ((!isDetails && genericPage) || (!isDetails && !genericPage && type === 'edit')) {
            compareKms();
            compareHours();
        }
    }, []);

    useEffect(() => {
        if (!isDetails && (vehicle || vehicleParam)) {
            // current kms from tracking
            getVehicleGpsCurrentKms();

        }
        setTypesOptions(
            Utils.sortAlphabetically(
                typesList.filter(x => {
                    if (x.value === VerificationType.TRAVEL) return showField(SEGMENT_FEATURES.KM);
                    if (x.value === VerificationType.HOURS_UTILIZATION) return showField(SEGMENT_FEATURES.HOURS);
                    return true;
                }),
            ),
        );
    }, [vehicle, vehicleParam]);

    useEffect(() => {
        if (((!!urlType && urlType == 'new') || (!!itemType && itemType == 'new'))) {
            setKilometers();
            setFinishKms();
            setHoursMeterValue();
            setFinishHoursValue();
        }
    }, [vehicleParam, currentKmsGps]);

    const setHoursMeterValue = () => {
        if (vehicleParam && showField(SEGMENT_FEATURES.HOURS) && typeWatch !== VerificationType.TRAVEL) {
            form.setValue('hoursMeter', vehicleParam?.currentHours ?? null);
            return;
        }
        form.setValue('hoursMeter', null);
    }

    const setFinishHoursValue = () => {
        if (vehicleParam && showField(SEGMENT_FEATURES.HOURS) && typeWatch !== VerificationType.TRAVEL && form.watch('verificationFinishDate') !== undefined) {
            form.setValue('hoursMeterFinish', vehicleParam?.currentHours ?? null);
            return;
        }
        form.setValue('hoursMeterFinish', null);
    }

    const setKilometers = () => {
        if (vehicleParam && showField(SEGMENT_FEATURES.KM)) {
            if (hasReadGPSKmsPolicy && currentKmsGps != null) {
                form.setValue('kilometers', currentKmsGps);
                return;
            }
            form.setValue('kilometers', vehicleParam?.currentKilometers);
            return;
        } else if (!showField(SEGMENT_FEATURES.KM)) {
            form.setValue('kilometers', undefined);
            return
        }
        form.setValue('kilometers', 0);

    }

    const setFinishKms = () => {
        if (vehicleParam && showField(SEGMENT_FEATURES.KM) && form.watch('verificationFinishDate') !== undefined) {
            if (hasReadGPSKmsPolicy && currentKmsGps != null) {
                form.setValue('kilometersFinish', currentKmsGps);
                return;
            }
            form.setValue('kilometersFinish', vehicleParam?.currentKilometers);
            return;
        }

        form.setValue('kilometersFinish', undefined);


    }
    useEffect(() => {
        useCloseWhenClickOutside({ showTime, elementId: 'timekeeper-start', onClose: setShowTime });
    }, [showTime]);

    useEffect(() => {
        useCloseWhenClickOutside({ showTime, elementId: 'timekeeper-finish', onClose: setShowTimeFinish });
    }, [showTimeFinish]);

    return (
        <div className={styles.verificationInfos}>
            <Row>
                {genericPage && <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{t('car_verification.vehicle')} *</Label>
                        <SelectController
                            form={form}
                            name='vehicleId'
                            menuPortalTarget={document.querySelector('body')}
                            options={vehicles}
                            placeholder={t('car_verification.vehicle')}
                            rules={{ required: !isDetails }}
                            isDisabled={isDetails}
                            filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                            onChangeSelect={(data: SelectValueLabel) => {
                                changeVehicle(data);
                            }}
                        />
                        <InputError error={errors.vehicleId} />
                    </FormItem>
                </Col>}
                <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{t('car_verification.verification.type')} *</Label>
                        <SelectController
                            form={form}
                            isClearable={true}
                            name='type'
                            menuPortalTarget={document.querySelector('body')}
                            options={typesOptions}
                            placeholder={t('car_verification.verification.type')}
                            rules={{ required: !isDetails }}
                            isDisabled={isDetails}
                            filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                            onChange={(item?: any) => {
                                if (!item || isTripLikeVerification(item?.value)) {
                                    form.setValue('annotationDate', null);
                                    form.setValue('predictedDateEndImmobilization', null);
                                    form.setValue('immobilized', false);
                                }

                                if (!item || !isTripLikeVerification(item?.value)) {
                                    form.setValue('kilometersFinish', undefined);
                                    form.setValue('hoursMeterFinish', null);
                                    form.setValue('verificationFinishDate', undefined);
                                    form.setValue('verificationFinishHour', undefined);
                                    setShowKilometersFinish(false);
                                    setFinishKmsBelowCurrentVehicleKms(false);
                                }

                                if (item?.value === VerificationType.TRAVEL) {
                                    form.setValue('hoursMeter', null);
                                    form.setValue('hoursMeterFinish', null);
                                }
                            }}
                        />
                        <InputError error={errors.type} />
                    </FormItem>
                </Col>
                {typeWatch == VerificationType.ROUTINE.toString() && <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{t('common.responsible')} *</Label>
                        <SelectController
                            form={form}
                            name='responsibleId'
                            menuPortalTarget={document.querySelector('body')}
                            options={responsables}
                            placeholder={t('common.responsible')}
                            rules={{ required: !isDetails }}
                            isDisabled={isDetails}
                            filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                        />
                        <InputError error={errors.responsibleId} />
                    </FormItem>
                </Col>}
                {isTripLikeVerification(typeWatch) && <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{t('car_verification.verification.driver')} *</Label>
                        <SelectController
                            form={form}
                            name='driverId'
                            menuPortalTarget={document.querySelector('body')}
                            options={drivers}
                            placeholder={t('car_verification.verification.driver')}
                            rules={{ required: !isDetails }}
                            isDisabled={isDetails}
                            filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                        />
                        <InputError error={errors.driverId} />
                    </FormItem>
                </Col>}
                <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>
                            {typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_date_start') : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_date_start') : t('car_verification.verification.verification_date')} *
                        </Label>
                        <DateTimePickerControllerNew
                            form={form}
                            placeholderText={typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_date_start') : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_date_start') : t('car_verification.verification.verification_date')}
                            name="verificationDate"
                            autoComplete='off'
                            disabled={isDetails}
                            //maxDate={new Date()}
                            rules={{ required: !isDetails }}
                        />
                        <InputError error={errors.verificationDate} />
                    </FormItem>
                </Col>
                <Col xs={6} lg={4} xl={3} style={{ maxHeight: '7rem' }}>
                    <FormItem>
                        <Label className={styles.label}>
                            {typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_hour_start') + ' *' : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_hour_start') + ' *' : t('car_verification.verification.verification_hour')}
                        </Label>
                        {showTime &&

                            <div style={{ zIndex: 1, position: 'relative', width: 'min-content' }} id="timekeeper-start">
                                <Timekeeper
                                    closeOnMinuteSelect={true}
                                    hour24Mode={true}
                                    switchToMinuteOnHourSelect
                                    doneButton={result => (
                                        <TimeKeeperDoneButton onClick={() => {
                                            setShowTime(false);
                                            form.setValue('verificationHour', `${result.hour > 9 ? result.hour : '0' + result.hour}:${result.minute > 9 ? result.minute : '0' + result.minute}`);
                                        }} />
                                    )}
                                />
                            </div>
                        }
                        {!showTime &&
                            <InputGroupController
                                autoComplete={'off'}
                                icon={<FaRegClock />}
                                placeholder={t('common.hour')}
                                name="verificationHour"
                                form={form}
                                rules={{
                                    required: isTripLikeVerification(typeWatch)
                                }}
                                disabled={isDetails}
                                onClick={() => setShowTime(true)}
                                isClearable={true}
                                onKeyDown={({ nativeEvent }) => {
                                    if (nativeEvent.key) {
                                        nativeEvent.preventDefault();
                                    }
                                }}
                            />}
                        <InputError error={errors.verificationHour} />
                    </FormItem>
                </Col>
                {showField(SEGMENT_FEATURES.KM) && <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_kilometers_start') + ' *' : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_kilometers_start') : t('common.kilometers')}</Label>
                        <InputGroupController
                            maxLength={255}
                            type="number"
                            text={t('common.kms')}
                            placeholder={typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_kilometers_start') : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_kilometers_start') : t('common.kilometers')}
                            name="kilometers"
                            form={form}
                            format="money"
                            disabled={isDetails}
                            rules={{ ...DEFAULT_INPUT_RULES_WITH_REQUIRED, validate: undefined, required: typeWatch == VerificationType.TRAVEL && showField(SEGMENT_FEATURES.KM) }}
                            onChange={() => compareKms()} />
                        <InputError error={errors.kilometers} />
                        {((!isDetails && form.getValues('vehicleId')) || (!isDetails && vehicleParam)) && <InputInfoWarning isWarning={inputKmsBelowCurrentVehicleKms} message={t((inputKmsBelowCurrentVehicleKms ? 'common.inserted_kms_below_current_kms' : 'common.insert_kms_below_current_kms'), { kms: Utils.numberFormatter(vehicleParam?.currentKilometers ?? 0) })} />}
                        {hasReadGPSKmsPolicy && ((!isDetails && form.getValues('vehicleId')) || (!isDetails && vehicleParam)) && currentKmsGps != null && <InputInfoWarning isWarning={false} message={t('location.current_km', { kms: Utils.numberFormatter(currentKmsGps ?? 0) })} />}
                    </FormItem>
                </Col>}
                {showField(SEGMENT_FEATURES.FUEL) && <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{t('car_verification.autonomy')}</Label>
                        <InputGroupController
                            type="number"
                            text={t('car_verification.autonomy_symbol')}
                            placeholder={t('car_verification.autonomy')}
                            name="autonomy"
                            form={form}
                            format="number"
                            disabled={isDetails}
                            rules={{ min: { value: 0, message: t('common.errors.min_value_0') }, max: { value: 100, message: t('common.errors.max_value_100') } }}
                        />
                        <InputError error={errors.autonomy} />
                    </FormItem>
                </Col>}
                {showField(SEGMENT_FEATURES.HOURS) && typeWatch !== VerificationType.TRAVEL && <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.hours_meter_start') + ' *' : t('common.hours')}</Label>
                        <InputGroupController
                            maxLength={255}
                            type="number"
                            text={t('common.hours_symbol')}
                            placeholder={typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.hours_meter_start') : t('common.hours')}
                            name="hoursMeter"
                            form={form}
                            format="number"
                            disabled={isDetails}
                            rules={{ required: typeWatch == VerificationType.HOURS_UTILIZATION && showField(SEGMENT_FEATURES.HOURS), min: { value: 0, message: t('common.errors.min_value_0') } }}
                            onChange={() => compareHours()}
                        />
                        <InputError error={errors.hoursMeter} />
                        {((!isDetails && form.getValues('vehicleId')) || (!isDetails && vehicleParam)) && <InputInfoWarning isWarning={inputHoursBelowCurrentVehicleHours} message={t((inputHoursBelowCurrentVehicleHours ? 'common.inserted_hours_below_current_hours' : 'common.insert_hours_below_current_hours'), { hours: Utils.numberFormatter(vehicleParam?.currentHours ?? 0) })} />}
                    </FormItem>
                </Col>}
            </Row>
            {isTripLikeVerification(typeWatch) && <Row>
                <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>
                            {typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_date_finish') : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_date_finish') : t('car_verification.verification.verification_date_finish')}
                        </Label>
                        <DateTimePickerControllerNew
                            form={form}
                            placeholderText={typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_date_finish') : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_date_finish') : t('car_verification.verification.verification_date_finish')}
                            name="verificationFinishDate"
                            autoComplete='off'
                            disabled={isDetails}
                            minDate={form.watch('verificationDate')}
                            //maxDate={new Date()}
                            rules={{ required: false }}
                            onChange={date => {
                                if (!!date) {
                                    setShowKilometersFinish(true)
                                }
                                else {
                                    setShowKilometersFinish(false)
                                    form.setValue('verificationFinishHour', '');
                                }
                                setFinishKms();
                                const currFinishHour = form.getValues('verificationFinishHour');
                                if (currFinishHour) {
                                    const currFinishHourSplit = currFinishHour.split(':');
                                    if (currFinishHourSplit && date &&
                                        dayjs(date as Date).set('hour', Number(currFinishHourSplit[0]))
                                            .set('minute', Number(currFinishHourSplit[1]))
                                            .toDate() > new Date()
                                    ) {
                                        form.setValue('verificationFinishHour', '');
                                    }
                                }
                            }}
                        />
                        <InputError error={errors.verificationFinishDate} />
                    </FormItem>
                </Col>
                <Col xs={6} lg={4} xl={3} style={{ maxHeight: '7rem' }}>
                    <FormItem>
                        <Label className={styles.label}>
                            {typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_hour_finish') + (verificationFinishDateWatch ? ' *' : '') : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_hour_finish') + (verificationFinishDateWatch ? ' *' : '') : t('car_verification.verification.verification_hour_finish')}
                        </Label>
                        {showTimeFinish &&
                            <div style={{ zIndex: 1, position: 'relative', width: 'min-content' }} id="timekeeper-finish">
                                <Timekeeper
                                    closeOnMinuteSelect={true}
                                    hour24Mode={true}
                                    switchToMinuteOnHourSelect
                                    doneButton={result => (
                                        <TimeKeeperDoneButton onClick={() => {
                                            setShowTimeFinish(false);
                                            form.setValue('verificationFinishHour', `${result.hour > 9 ? result.hour : '0' + result.hour}:${result.minute > 9 ? result.minute : '0' + result.minute}`);
                                        }} />
                                    )}
                                    disabledTimeRange={
                                        verificationFinishDateWatch && dayjs(verificationFinishDateWatch).isSame(form.watch('verificationDate'), 'day')
                                            ? { from: '00:00', to: form.watch('verificationHour') }
                                            : undefined
                                    }
                                />
                            </div>
                        }
                        {!showTimeFinish &&
                            <InputGroupController
                                autoComplete={'off'}
                                icon={<FaRegClock />}
                                placeholder={t('common.hour')}
                                name="verificationFinishHour"
                                form={form}
                                rules={{
                                    required: verificationFinishDateWatch != null && verificationFinishDateWatch != undefined
                                }}
                                disabled={isDetails || !verificationFinishDateWatch}
                                onClick={() => setShowTimeFinish(true)}
                                isClearable={true}
                                onKeyDown={({ nativeEvent }) => {
                                    if (nativeEvent.key) {
                                        nativeEvent.preventDefault();
                                    }
                                }}
                            />}
                        <InputError error={errors.verificationFinishHour} />
                    </FormItem>
                </Col>
                {showKilometersFinish && showField(SEGMENT_FEATURES.KM) && <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_kilometers_finish') : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_kilometers_finish') : t('car_verification.verification.kilometers_finish')} {'*'}</Label>
                        <InputGroupController
                            maxLength={255}
                            type="number"
                            text={t('common.kms')}
                            placeholder={typeWatch == VerificationType.TRAVEL ? t('car_verification.verification.trip_kilometers_finish') : typeWatch == VerificationType.HOURS_UTILIZATION ? t('car_verification.verification.utilization_kilometers_finish') : t('car_verification.verification.kilometers_finish')}
                            name="kilometersFinish"
                            form={form}
                            format="money"
                            disabled={isDetails}
                            rules={{ required: true, validate: undefined }}
                            onChange={() => compareKms()} />
                        <InputError error={errors.kilometersFinish} />
                        {((!isDetails && form.getValues('vehicleId')) || (!isDetails && vehicleParam)) && <InputInfoWarning isWarning={finishKmsBelowCurrentVehicleKms} message={t((finishKmsBelowCurrentVehicleKms ? 'common.inserted_kms_below_current_kms' : 'common.insert_kms_below_current_kms'), { kms: Utils.numberFormatter(vehicleParam?.currentKilometers ?? 0) })} />}
                        {hasReadGPSKmsPolicy && ((!isDetails && form.getValues('vehicleId')) || (!isDetails && vehicleParam)) && currentKmsGps != null && <InputInfoWarning isWarning={false} message={t('location.current_km', { kms: Utils.numberFormatter(currentKmsGps ?? 0) })} />}
                    </FormItem>
                </Col>}
                {showKilometersFinish && showField(SEGMENT_FEATURES.HOURS) && typeWatch !== VerificationType.TRAVEL && <Col xs={6} lg={4} xl={3}>
                    <FormItem>
                        <Label className={styles.label}>{t('car_verification.verification.hours_meter_finish')} *</Label>
                        <InputGroupController
                            maxLength={255}
                            type="number"
                            text={t('common.hours_symbol')}
                            placeholder={t('car_verification.verification.hours_meter_finish')}
                            name="hoursMeterFinish"
                            form={form}
                            format="number"
                            disabled={isDetails}
                            rules={{ required: true, min: { value: 0, message: t('common.errors.min_value_0') } }}
                            onChange={() => compareHours()}
                        />
                        <InputError error={errors.hoursMeterFinish} />
                        {((!isDetails && form.getValues('vehicleId')) || (!isDetails && vehicleParam)) && <InputInfoWarning isWarning={finishHoursBelowCurrentVehicleHours} message={t((finishHoursBelowCurrentVehicleHours ? 'common.inserted_hours_below_current_hours' : 'common.insert_hours_below_current_hours'), { hours: Utils.numberFormatter(vehicleParam?.currentHours ?? 0) })} />}
                    </FormItem>
                </Col>}

            </Row>}
            <Row>
                <Col xs={6} lg={4} xl={3}>
                    <Label className={styles.label}>{t('car_verification.immobilized')}</Label>
                    <ToggleSwitch
                        name={'immobilized'}
                        form={form}
                        isDetails={isDetails}
                        changeToggle={() => { form.setValue('predictedDateEndImmobilization', null) }}
                    />
                </Col>
                <Col xs={6} lg={4} xl={3}>
                    <Label className={styles.label}>{t('car_verification.predicted_date_end_immobilization')}
                        <span style={{ marginLeft: '0.5rem' }}>
                            <FaInfoCircle data-tip={t('car_verification.predicted_date_end_immobilization_message')} data-for="date_end_immobilization_message" />
                            <ReactTooltip multiline={true} id="date_end_immobilization_message"/>
                        </span>
                    </Label>
                    <DateTimePickerControllerNew
                        form={form}
                        placeholderText={t('car_verification.predicted_date_end_immobilization')}
                        name={'predictedDateEndImmobilization'}
                        autoComplete='off'
                        disabled={isDetails || !form.watch('immobilized')}
                    />
                </Col>
            </Row>

        </div>
    );
}

export default InformationForm;
